import type { Promisable } from 'type-fest'
import { sleep } from './sleep'

/**
 * Create a timeout-based polling task
 */
export async function polling<T>(
  /**
   * The task to run during each polling attempt. The polling stops when this
   * function returns any value other than `undefined`.
   */
  predicate: () => Promisable<T | undefined>,
  opts?: {
    /**
     * Maximum number of polling attempts. Defaults to `Infinity` (endless).
     */
    maxAttempts?: number
    /**
     * The timeout between two polling attempts. Can be used to control the
     * polling frequency. Defaults to `0` (no timeout).
     */
    timeout?: number
  },
): Promise<T | undefined> {
  const { maxAttempts = Infinity, timeout = 0 } = opts ?? {}

  return Promise.resolve().then(async () => {
    let attempts = 0

    do {
      if (attempts && timeout) await sleep(timeout)

      const value = await predicate()
      if (value !== undefined) return value
    } while (++attempts < maxAttempts)
  })
}
